// @ts-strict-ignore
/** @jsx jsx */
/** @jsxFrag null */
import {jsx} from 'panel';
import sum from 'lodash/sum';
import type {VNode} from 'snabbdom';
import isNumber from 'lodash/isNumber';

import {ButtonEl} from 'common/components/button';
import {ButtonTheme} from 'common/components/button/constants';
import {CheckboxEl} from 'common/components/checkbox';
import {
  CHECKBOX_CELL_PADDING_LEFT,
  CssClass,
  DEFAULT_TOOLTIP_MAX_WIDTH,
  FOOTER_ROW_HEIGHT,
  GROUP_HEADER_MIN_WIDTH,
  STICKY_COLUMNS_BORDER_WIDTH,
} from 'common/components/data-table/constants';
import {SelectTheme} from 'common/components/select/constants';
import {SelectEl} from 'common/components/select';
import {SvgIconEl} from 'common/components/svg-icon';
import {ExperimentalTooltipEl} from 'common/components/tooltip';
import {Placement} from 'common/components/tooltip/types';
import {SortBy, SortOrder} from 'common/types/reports/bookmark';
import {isURL} from 'common/util/url';
import {renderSegmentLabel} from 'common/templates/mixins';
import {InputEl} from 'common/components/input';
import {InputSize, InputType} from 'common/components/input/types';
import {HORIZONTAL_CONTENT_PADDING_STYLE} from 'iron/common/report/layout/banner-layout';
import {FilterBarEl} from 'common/components/filter-bar';
import {getTooltipFromLabel} from 'common/report/util/properties';
import {SIZE, TEXT_HIGHLIGHT_THEME, TextHighlightEl} from 'common/components/text-highlight';
import {LIFT_TOOLTIP_COMPARE, LiftType} from 'common/report/util/lift/types';
import {formatDateRanges} from 'common/report/insights/util';
import {Translatable} from 'common/i18n';
import {LocalizationStore} from 'common/i18n/store';

import type {DataTable} from './index';
import {
  BodyCellRenderContext,
  CommonDataTableFields,
  DataColumn,
  DataFormatType,
  GroupHeaderCellRenderContext,
  HeaderCellRenderContext,
  isTimeComparisonColumnType,
  RowType,
  SegmentCellBlock,
  SegmentMetricMetadata,
  TableColumn,
  TableRow,
  TimeComparisonColumnType,
  TimeComparisonHeaderCellRenderContext,
  TruncatableTextType,
} from './types';
import {
  areIntersectingRanges,
  formatPercentForDataTable,
  getFormattedValue,
  getSummedColumnWidth,
  hasSegmentMetricMetadata,
  isNumberStyleColumn,
  isSameCellByContext,
  isSegmentColumn,
  isTextStyleColumn,
} from './utils';
import segmentMetricOverflowTooltipStyle from './segment-metric-overflow-tooltip.inline.styl';
import viewNMenuStyle from './view-n-menu.inline.styl';

function renderViewNMenu({tableEl, segmentIdx}: {tableEl: DataTable; segmentIdx: number}) {
  const isColumnSort = tableEl.state.sorting.sortBy === SortBy.Column;
  const hasViewNLimit = tableEl.hasViewNLimit({segmentIdx});

  return (
    <div sel='.view-n-menu-content'>
      <style>{viewNMenuStyle}</style>
      <div sel='.view-n-menu-limit-section'>
        <div sel='.view-n-menu-title'>{`${tableEl.getViewNSortOrderLabel({segmentIdx})} ${
          isColumnSort ? `Segments` : `Values`
        }`}</div>
        <div sel='.view-n-menu-description'>
          {`Choose the number of ${isColumnSort ? `segments` : `rows`} you want to see in the chart`}
        </div>
        <div sel='.view-n-menu-input-section' class={{'show-all-mode': !hasViewNLimit}}>
          <InputEl
            sel='.view-n-menu-input'
            attrs={{
              'auto-focus': hasViewNLimit,
              'not-focusable': !hasViewNLimit,
              size: InputSize.Medium,
              type: InputType.Number,
              min: `1`,
              maxlength: 5,
            }}
            on={{
              focus: () => tableEl.handleViewNLimitInputFocus({segmentIdx}),
              blur: () => tableEl.handleViewNLimitInputBlur(),
              change: (ev) =>
                tableEl.handleViewNLimitInputChange({segmentIdx, value: Number.parseInt(ev.detail.value, 10)}),
            }}
            props={{
              value: tableEl.getViewNLimitInputValue({segmentIdx}).toString(),
            }}
          />
          <div sel='.view-n-menu-input-label'>{isColumnSort ? `segments` : `rows`}</div>
        </div>
      </div>
      <div sel='.view-n-show-all-section'>
        <CheckboxEl
          attrs={{
            checked: !hasViewNLimit,
          }}
          on={{
            click: () => tableEl.handleViewNShowAllClick({segmentIdx}),
          }}
        >
          Show all {isColumnSort ? `segments` : `rows`}
        </CheckboxEl>
      </div>
    </div>
  );
}

function renderViewNControl({tableEl, segmentIdx}: {tableEl: DataTable; segmentIdx: number}) {
  const {sortColumn, sortOrder} = tableEl.getViewNSortInfo({segmentIdx});
  const sortFieldStr = isSegmentColumn(sortColumn) ? sortColumn.title : sortColumn.sortSelectTitle;
  const sortOrderStr = sortOrder === SortOrder.Ascending ? `ascending` : `descending`;
  return (
    <SelectEl
      sel='.view-n-select'
      attrs={{
        'experimental-positioning': true,
        theme: SelectTheme.Tertiary,
        'always-show-caret': true,
        disabled: tableEl.isBoardMode(),
        label: tableEl.getViewNLabel({segmentIdx}),
        ...(tableEl.hasViewNLimit({segmentIdx}) && {
          tooltip: {
            placement: Placement.RightCenter,
            'max-width': `${DEFAULT_TOOLTIP_MAX_WIDTH}px`,
            text: `Sorted by ${sortFieldStr} ${sortOrderStr}`,
          },
        }),
      }}
      props={{
        // body slot doesn't work with portalled mp-drop-menu, so need to provide render function instead
        dropdownBodyRenderer: () => renderViewNMenu({tableEl, segmentIdx}),
      }}
      on={{
        menuChange: (ev) => tableEl.handleViewNMenuChange({ev, segmentIdx}),
      }}
    />
  );
}

//#region helper mixins

function TitleCell({tableEl, column}: {tableEl: DataTable; column: TableColumn}) {
  if (
    isTimeComparisonColumnType(column) &&
    column.timeComparisonMetadata.timeComparisonColumnType === TimeComparisonColumnType.Past
  ) {
    return null;
  }
  const {print} = tableEl.getContext(`i18n`);

  const context: HeaderCellRenderContext = {rowType: RowType.Header, columnIdx: column.columnIdx};
  const isHoveredCell = isSameCellByContext(context, tableEl.state.hoveredCellContext);

  const {sorting} = tableEl.state;
  const isCurrSortField = sorting.sortBy === SortBy.Value && sorting.valueField === column.field;

  let headerTooltipEl: VNode = null;
  if (isHoveredCell) {
    let tooltipContent: Translatable = null;
    if (column.tooltip) {
      tooltipContent = column.tooltip;
    } else if (tableEl.state.hoveredTruncatedTextType === TruncatableTextType.HeaderTitle) {
      tooltipContent = column.title;
    } else if (tableEl.state.hoveredTruncatedTextType === TruncatableTextType.HeaderSubtitle) {
      tooltipContent = column.subtitle;
    }
    headerTooltipEl = tooltipContent ? (
      <ExperimentalTooltipEl
        attrs={{
          // The tooltip of the first data column header is constrained to prevent it from being obscured by the
          // sticky column to its left.
          'max-width':
            !column.sticky && column.subsetColumnIdx === 0
              ? `${column.computedWidth}px`
              : `${DEFAULT_TOOLTIP_MAX_WIDTH}px`,
          placement: Placement.Top,
          'always-show': true,
        }}
        params={{
          text: tooltipContent,
        }}
      />
    ) : null;
  }

  const titleEl = (
    <div
      class={{[CssClass.HeaderTitleText]: true, 'cell-text': true}}
      on={{
        pointerenter: (ev) => tableEl.helpers.handleTruncatableTextPointerEnter(ev, TruncatableTextType.HeaderTitle),
        pointerleave: () => tableEl.helpers.handleTruncatableTextPointerLeave(),
      }}
    >
      {column.titlePrefix ? <span sel='.header-title-prefix'>{column.titlePrefix}</span> : null}
      <span attrs={{elref: `headerCellTitle`}}>{print(column.title)}</span>
    </div>
  );

  let subtitleEl: VNode = null;
  if (column.subtitle) {
    subtitleEl = (
      <div
        class={{[CssClass.HeaderSubtitleText]: true, 'cell-text': true}}
        on={{
          pointerenter: (ev) =>
            tableEl.helpers.handleTruncatableTextPointerEnter(ev, TruncatableTextType.HeaderSubtitle),
          pointerleave: () => tableEl.helpers.handleTruncatableTextPointerLeave(),
        }}
      >
        {column.subtitle}
      </div>
    );
  } else if (isSegmentColumn(column) && tableEl.canRenderViewNControl({segmentColumn: column})) {
    subtitleEl = renderViewNControl({tableEl, segmentIdx: column.segmentIndex});
  } else if (tableEl.hasColumnWithSubtitle()) {
    subtitleEl = <div sel='.header-subtitle-placeholder' />;
  }

  let titleLabelContents: VNode = null;
  if (column.title) {
    if (!column.isSortable) {
      titleLabelContents = titleEl;
    } else if (isSegmentColumn(column)) {
      titleLabelContents = (
        <SelectEl
          attrs={{
            'menu-width': 190,
            'experimental-positioning': true,
          }}
          on={{
            change: tableEl.helpers.handleSortSelect,
          }}
          props={{
            sections: tableEl.getSortSelectSections(column),
          }}
        >
          <div attrs={{slot: `trigger`}} sel='.sort-label.within-select-el'>
            {titleEl}
            {isSegmentColumn(column) ? <span sel='.segment-cardinality'>{column.segmentCardinality}</span> : null}
            <div sel='.sort-icon'>
              <SvgIconEl attrs={{icon: `caret-down`}} />
            </div>
          </div>
        </SelectEl>
      );
    } else {
      titleLabelContents = (
        <div sel='.sort-label.value-sort'>
          {titleEl}
          {isCurrSortField ? (
            sorting.sortOrder === SortOrder.Ascending ? (
              <div sel='.sort-icon'>
                <SvgIconEl attrs={{icon: `sort-value-asc`}} />
              </div>
            ) : (
              <div sel='.sort-icon'>
                <SvgIconEl attrs={{icon: `sort-value-desc`}} />
              </div>
            )
          ) : null}
        </div>
      );
    }
  }

  const hasSelectAllCheckbox = column.hasCheckbox;
  const titleContents = (
    <div
      sel='.mp-table-title-wrapper'
      on={{
        ...(isSegmentColumn(column) ? {} : {click: () => tableEl.helpers.handleHeaderTitleClick(column)}),
      }}
    >
      {hasSelectAllCheckbox
        ? (() => {
            const {numSelected, atSelectionLimit} = tableEl.helpers.getGlobalSelectionStats();
            return (
              <div sel='.checkbox-wrapper'>
                <CheckboxEl
                  sel='.select-segment-checkbox'
                  attrs={{
                    checked: atSelectionLimit,
                    'unchecked-icon': numSelected > 0 ? `dash` : ``,
                  }}
                  style={{width: `auto`}}
                  on={{
                    click: tableEl.helpers.toggleAllSelected,
                    pointerenter: tableEl.helpers.handleSelectAllPointerEnter,
                    pointerleave: tableEl.helpers.handleSelectAllPointerLeave,
                  }}
                />
                {tableEl.state.isSelectAllHovered ? (
                  <ExperimentalTooltipEl
                    attrs={{
                      placement: Placement.RightCenter,
                      'always-show': true,
                      text: atSelectionLimit ? `Deselect All` : `Select Top Segments`,
                    }}
                  />
                ) : null}
              </div>
            );
          })()
        : null}
      {titleLabelContents}
      {column.icon ? (
        <div sel='.mp-table-title-cell-icon'>
          <SvgIconEl attrs={{icon: column.icon}} />
        </div>
      ) : null}
    </div>
  );

  // For time comparison columns we need to double the size of one of the title cells
  // and hide the other one.
  const gridCellWidth =
    isTimeComparisonColumnType(column) &&
    column.timeComparisonMetadata.timeComparisonColumnType === TimeComparisonColumnType.Current
      ? 2
      : 1;

  const gridRowStart = (Number.isFinite(tableEl.getPreTitleRowHeight()) ? 1 : 0) + 1;
  const gridColStart = column.subsetColumnIdx + 1;
  const gridAreaStyle = `${gridRowStart} / ${gridColStart} / ${gridRowStart + 1} / ${gridColStart + gridCellWidth}`;

  return (
    <div
      sel='.mp-table-cell.title-cell'
      class={{
        'mp-table-sortable-column': column.isSortable,
        'mp-text-cell': isTextStyleColumn(column),
        'mp-number-cell': isNumberStyleColumn(column),
        'segment-col': isSegmentColumn(column),
        'first-segment-col': column.field === `${CommonDataTableFields.SegmentPrefix}0`,
        'first-in-group': column.first,
        'last-in-group': column.last,
        'overall-conversion-header': column.field === `overall`,
        'first-column': column.leftmost,
        'last-column': column.rightmost,
        'last-sticky': column.lastSticky,
        'last-segment-col': isSegmentColumn(column) && column.lastSegment,
        'sticky-column': column.sticky,
        'has-tooltip': Boolean(headerTooltipEl || tableEl.state.isSelectAllHovered),
        'curr-sort-field': isCurrSortField,
        'has-subtitle': Boolean(subtitleEl),
      }}
      style={{
        'grid-area': gridAreaStyle,
      }}
      on={{
        pointerenter: () => tableEl.helpers.handleCellPointerEnter(context),
        pointerleave: tableEl.helpers.handleCellPointerLeave,
      }}
      attrs={{
        title: column.label || ``,
      }}
    >
      {titleContents}
      {subtitleEl}
      {headerTooltipEl}
    </div>
  );
}

function CellTypeCheckbox({
  row,
  tableEl,
  atSelectionLimit,
  context,
}: {
  tableEl: DataTable;
  row: TableRow;
  atSelectionLimit: boolean;
  context: BodyCellRenderContext;
}) {
  const options = tableEl.helpers.getCheckboxState({row, atSelectionLimit});
  const shouldShowTooltip = isSameCellByContext(context, tableEl.state.hoveredCellContext);

  return !row.isComparisonRow && !Number.isFinite(row.groupedRowIdx) ? (
    <>
      <CheckboxEl
        sel='.select-segment-checkbox'
        attrs={options.attrs}
        on={{click: (ev) => !options.attrs.disabled && tableEl.helpers.toggleSelected(ev, row)}}
      />
      {shouldShowTooltip && options.selectDisabledReason && options.attrs.disabled ? (
        <ExperimentalTooltipEl
          attrs={{
            'always-show': true,
            placement: Placement.RightCenter,
            text: options.selectDisabledReason,
          }}
        />
      ) : null}
    </>
  ) : (
    <div sel='.checkbox-placeholder' />
  );
}

function CellTypeCollapsible({row, tableEl}: {row: TableRow; tableEl: DataTable}) {
  return (
    <div sel='.mp-table-collapsible-wrapper'>
      {!Number.isFinite(row.groupedRowIdx) ? (
        <ButtonEl
          attrs={{
            icon: row.isCollapsed ? `caret-right` : `caret-down`,
            theme: ButtonTheme.Tertiary,
          }}
          on={{
            click: (ev) => tableEl.helpers.collapsibleClicked(ev, row),
          }}
        />
      ) : null}
    </div>
  );
}

function renderSegmentMetricMetadataRow({
  metadata,
  dataFormatType,
  liftType,
  metricTitle,
  prefix,
  i18n,
  inlineMetric = false,
}: {
  metadata: SegmentMetricMetadata;
  dataFormatType: DataFormatType;
  liftType?: LiftType;
  metricTitle: Translatable;
  prefix: string;
  i18n: LocalizationStore;
  inlineMetric?: boolean;
}) {
  const {comparisonOverall, liftOverall, metricOverall} = metadata;

  const formattedMetricOverall = getFormattedValue(metricOverall, dataFormatType);
  const formattedComparisonOverall = getFormattedValue(comparisonOverall, dataFormatType);

  // displays the lift baseline comparison
  const additionalSummaryText = Number.isFinite(liftOverall)
    ? ` (${LIFT_TOOLTIP_COMPARE} ${formattedComparisonOverall})`
    : ``;

  return (
    <div sel='.segment-metric-total-row'>
      <span sel='.segment-metric-total-label'>
        {prefix}
        {inlineMetric ? ` ${i18n.print(metricTitle)}` : ``}
      </span>
      <span sel='.segment-metric-total-value'>{formattedMetricOverall}</span>
      {Number.isFinite(liftOverall) && (
        <TextHighlightEl
          sel='.segment-metric-lift-value'
          attrs={{
            text: `${liftOverall > 0 && liftType !== LiftType.Overall ? `+` : ``}${formatPercentForDataTable(
              liftOverall,
            )}`,
            theme:
              liftOverall === 0 || liftType === LiftType.Overall
                ? TEXT_HIGHLIGHT_THEME.NEUTRAL
                : liftOverall > 0
                  ? TEXT_HIGHLIGHT_THEME.POSITIVE
                  : TEXT_HIGHLIGHT_THEME.NEGATIVE,
            size: SIZE.SMALL,
          }}
        />
      )}
      {inlineMetric ? (
        additionalSummaryText
      ) : (
        <ExperimentalTooltipEl
          attrs={{
            placement: Placement.RightCenter,
            text: `_${prefix} ${metricTitle}_ ${formattedMetricOverall}${additionalSummaryText}`,
          }}
        />
      )}
    </div>
  );
}

function renderSegmentMetricMetadatas({
  dataColumns,
  liftType,
  metricMetadatas,
  numAvailableRows,
  i18n,
}: {
  dataColumns: DataColumn[];
  liftType?: LiftType;
  metricMetadatas: SegmentMetricMetadata[];
  numAvailableRows: number;
  i18n: LocalizationStore;
}) {
  const metadataEntries = metricMetadatas
    .map((metadata, index) => {
      return {
        dataFormatType: dataColumns[index].dataFormatType,
        liftType,
        metadata,
        metricTitle: dataColumns[index].title,
        prefix: dataColumns[index].titlePrefix,
      };
    })
    .filter((entry) => hasSegmentMetricMetadata(entry.metadata));

  const hasHiddenRows = metadataEntries.length > numAvailableRows;
  const numMetricRowsToRender = hasHiddenRows ? numAvailableRows - 1 : metricMetadatas.length;
  const visibleMetadatas = metadataEntries.slice(0, numMetricRowsToRender);
  const hiddenMetadatas = metadataEntries.slice(numMetricRowsToRender);

  return (
    <div sel='.segment-metric-totals-section'>
      {visibleMetadatas.map((entry) => renderSegmentMetricMetadataRow({...entry, i18n}))}
      {hiddenMetadatas?.length > 0 ? (
        <div sel='.segment-metric-total-more-section'>
          <span sel='.segment-metric-total-more'>{hiddenMetadatas.length} more</span>
          <ExperimentalTooltipEl
            attrs={{
              placement: Placement.RightCenter,
            }}
          >
            <div sel='.segment-metric-overflow-content'>
              <style>{segmentMetricOverflowTooltipStyle}</style>
              {hiddenMetadatas.map((entry) => renderSegmentMetricMetadataRow({...entry, i18n, inlineMetric: true}))}
            </div>
          </ExperimentalTooltipEl>
        </div>
      ) : null}
    </div>
  );
}

function renderDataCellContent({
  liftType,
  formattedValue,
  liftValue,
  formattedComparisonValue,
}: {
  liftType: LiftType;
  formattedValue?: string;
  liftValue?: number;
  formattedComparisonValue?: number | string;
  dataFormatType?: DataFormatType;
}): VNode | string {
  if (!isNumber(formattedValue) && !formattedValue) {
    return `-`;
  }

  if (Number.isFinite(liftValue)) {
    const formattedLiftValue = formatPercentForDataTable(liftValue);
    const comparisonTooltip = (
      <ExperimentalTooltipEl
        attrs={{
          placement: Placement.Top,
          text: `${formattedValue} ${LIFT_TOOLTIP_COMPARE} ${formattedComparisonValue}`,
        }}
      />
    );

    if (liftType === LiftType.Overall) {
      return (
        <>
          <div sel='.cell-text'>
            <span sel='.cell-text.is-secondary'>{`${formattedValue} · `}</span>
            {comparisonTooltip}
            {`${formattedLiftValue}`}
          </div>
        </>
      );
    }

    const isPositive = liftValue > 0;

    return (
      <>
        <div sel='.cell-text'>
          {comparisonTooltip}
          {formattedValue}
          <TextHighlightEl
            sel='.mp-lift-pill'
            attrs={{
              text: `${isPositive ? `+` : ``}${formattedLiftValue}`,
              theme:
                liftValue === 0
                  ? TEXT_HIGHLIGHT_THEME.NEUTRAL
                  : isPositive
                    ? TEXT_HIGHLIGHT_THEME.POSITIVE
                    : TEXT_HIGHLIGHT_THEME.NEGATIVE,
              size: SIZE.SMALL,
            }}
          />
        </div>
      </>
    );
  }

  return formattedValue;
}

function TimeComparisonHeaderCell({column, tableEl}: {column: TableColumn; tableEl: DataTable}) {
  // For time comparison columns we need to double the size of one of the title cells
  // and hide the other one.
  const gridCellWidth = 1;

  const gridRowStart = tableEl.getNumHeaderRows();
  const gridColStart = column.subsetColumnIdx + 1;
  const gridAreaStyle = `${gridRowStart} / ${gridColStart} / ${gridRowStart + 1} / ${gridColStart + gridCellWidth}`;

  let formattedDateRange = ``;
  let prefix = ``;
  let titleAttributeText = ``;
  if (column.timeComparisonMetadata) {
    [formattedDateRange] = formatDateRanges([column.timeComparisonMetadata.dateRange]);
    prefix =
      column.timeComparisonMetadata.timeComparisonColumnType === TimeComparisonColumnType.Current ? `Current` : `Past`;
    titleAttributeText = `${prefix} ${formattedDateRange}`;
  }

  const context: TimeComparisonHeaderCellRenderContext = {
    columnIdx: column.columnIdx,
    rowType: RowType.TimeComparisonHeader,
  };

  return (
    <div
      attrs={{title: titleAttributeText}}
      class={{
        'first-in-group': column.first,
        'last-in-group': column.last,
        'first-column': column.leftmost,
        'last-column': column.rightmost,
        'last-sticky': column.lastSticky,
      }}
      on={{
        pointerenter: () => tableEl.helpers.handleCellPointerEnter(context),
        pointerleave: tableEl.helpers.handleCellPointerLeave,
      }}
      sel='.mp-table-cell.mp-time-comparison-cell'
      style={{'grid-area': gridAreaStyle}}
    >
      {column.timeComparisonMetadata ? (
        <div sel='.time-comparison-cell-wrapper'>
          <div sel='.time-comparison-cell-content'>
            <div sel='.overflow-wrapper'>
              <span sel='.mp-time-comparison-prefix'>{prefix}</span>
              {formattedDateRange}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function renderBodyRows({
  tableEl,
  rows,
  columns,
  atSelectionLimit,
}: {
  tableEl: DataTable;
  rows: TableRow[];
  columns: TableColumn[];
  atSelectionLimit: boolean;
}) {
  return rows.map((row) => {
    return columns.map((column) => {
      return <BodyCell tableEl={tableEl} row={row} column={column} atSelectionLimit={atSelectionLimit} />;
    });
  });
}

function BodyCell(props: {
  tableEl: DataTable;
  row: TableRow;
  column: TableColumn;
  atSelectionLimit: boolean;
  segmentCellBlock?: SegmentCellBlock;
}) {
  const {tableEl, row, column, atSelectionLimit, segmentCellBlock} = props;

  const context = tableEl.helpers.getBodyCellRenderContext(row, column);
  const cellItem = tableEl.helpers.getCellItem(row, column.columnIdx);
  const empty = cellItem === null;
  const bgColor = (cellItem && cellItem.backgroundColor) || column.backgroundColor || null;
  const textColor = (cellItem && cellItem.textColor) || column.textColor || null;
  const tableTheme = tableEl.getTheme();
  const focusable = tableTheme.cellFocusable && !empty;

  let cellContent: string | VNode | React.ReactNode;
  let cellTooltipEl: string | VNode = null;
  let cellCheckboxEl: string | VNode = null;
  let cellCollapseEl: string | VNode = null;

  let isUrlSegmentCell = false;
  if (column.cellRenderer) {
    cellContent = column.cellRenderer({item: row, context});
  } else if (isSegmentColumn(column)) {
    if (isURL(context.label)) {
      cellContent = renderSegmentLabel(context.label);
      isUrlSegmentCell = true;
    } else {
      cellContent = context.label;
    }
  } else {
    if (tableEl.isMultimetricMode()) {
      // TODO(mack): Should move all usages of mp-data-table to stop using cellRenderer (which is used for lift mode)
      // and to instead have mp-data-table handle the rendering, which gives mp-data-table more control for handling
      // concerns like tooltip z-index issues.
      // TODO(mack): Consider moving tooltip rendering that happens within renderDataCellContent to be part of
      // the cellTooltipEl generation that happens below.
      cellContent = renderDataCellContent({
        liftType: tableEl.attr(`lift-comparison-type`),
        formattedValue: context.label,
        liftValue: row[context.liftField] ?? null,
        formattedComparisonValue: row[context.liftExtra],
        dataFormatType: column.dataFormatType,
      });
    } else {
      cellContent = context.label;
    }
  }

  /**
   * @pablo
   * tooltips are if-gated for the hovered cell, this results in different tooltip behavior for the table where
   * tooltip transitions for rapidly hovering over multiple elements is disabled, and fade out animations are lost
   */
  if (isSameCellByContext(context, tableEl.state.hoveredCellContext)) {
    let tooltipContent: string | VNode | React.ReactNode = null;
    const defaultTooltip = getTooltipFromLabel(context.label);

    if (cellItem?.tooltip) {
      tooltipContent = cellItem.tooltip;
    } else if (isSegmentColumn(column)) {
      const {print} = tableEl.getContext(`i18n`);
      const isUnderSegmentCellBlock = tableEl.showSegmentCellBlocks() && !segmentCellBlock && !column.lastSegment;
      if (isUnderSegmentCellBlock) {
        // Avoids double tooltips on segment cell blocks by removing tooltips for the hidden BodyCell underneath
        tooltipContent = null;
      } else if (tableEl.state.hoveredTruncatedTextType) {
        // Only the segment columns should contain arbitrary content that can overflow and get truncated (thus
        // requiring a tooltip).

        // If the cellContent is a link, render just the url in the tooltip rather than the <a> element.
        tooltipContent = isUrlSegmentCell ? context.label : print(defaultTooltip) || cellContent;
      } else {
        tooltipContent = print(defaultTooltip);
      }
    }
    const stringTooltip = typeof tooltipContent === `string`;
    cellTooltipEl = Boolean(tooltipContent) && (
      <ExperimentalTooltipEl
        attrs={{
          'always-show': true,
          'max-width': `${DEFAULT_TOOLTIP_MAX_WIDTH}px`,
          placement: Placement.RightCenter,
          'break-word': true,
          text: stringTooltip ? (tooltipContent as string) : ``,
        }}
      />
    );
  }

  // funnels statSig cells don't play well with the overflow wrapper (causes the clickable icon to stack above text)
  if (column.field !== `statSig`) {
    cellContent = (
      <div
        class={{[CssClass.OverflowWrapper]: true}}
        // Title attribute helps for overflowing number values if the user turns
        // off abbreviation with a small table on screen. But we don't want to
        // double up functionality for places where we're already displaying a
        // tooltip.
        attrs={{title: !cellTooltipEl && typeof cellContent === `string` ? cellContent : ``}}
        on={{
          pointerenter: (ev) => tableEl.helpers.handleTruncatableTextPointerEnter(ev),
          pointerleave: () => tableEl.helpers.handleTruncatableTextPointerLeave(),
        }}
      >
        {cellContent}
      </div>
    );
  }

  if (column.hasCheckbox) {
    cellCheckboxEl = (
      <CellTypeCheckbox row={row} tableEl={tableEl} atSelectionLimit={atSelectionLimit} context={context} />
    );
  }
  if (column.hasCollapseButton) {
    cellCollapseEl = <CellTypeCollapsible row={row} tableEl={tableEl} />;
  }

  let gridAreaStyle: string = null;
  if (!segmentCellBlock) {
    // We want to calculate the row relative to all rows (including expanded
    // rows) since the CSS grid plots all rows. To get that val we add the index
    // of the current row relative to the subset of rows that are displayed
    // (renderRowIdx) to the index of the first rendered row relative to all
    // rows (renderRowStartIdx).
    const rowIdx = row.renderRowIdx + tableEl.state.renderRowStartIdx;
    const gridRowStart = tableEl.getNumHeaderRows() + rowIdx + 1;
    const gridColStart = column.subsetColumnIdx + 1;
    gridAreaStyle = `${gridRowStart} / ${gridColStart} / ${gridRowStart + 1} / ${gridColStart + 1}`;
  }

  const isSelectedCell = focusable && tableEl.isSelectedCell(context);
  const isSelectable = column.isSelectable && !tableEl.isBoardMode();

  return (
    <div
      sel='.mp-table-cell.body-cell'
      // Avoid snabbdom reusing old cells for new data. This avoid the following issues:
      // - cells occasionally showing stale content if the user hovers while sort is changing
      // - flickering of the selected segment checkboxes while scrolling
      key={`${row.rowIdx}:${row.groupedRowIdx}:${column.columnIdx}:${context.label}`}
      class={{
        'first-in-group': column.first,
        'last-in-group': column.last,
        'first-column': column.leftmost,
        'last-column': column.rightmost,
        'last-sticky': column.lastSticky,
        'sticky-column': column.sticky,
        'last-segment-col': isSegmentColumn(column) && column.lastSegment,
        'segment-col': isSegmentColumn(column),
        'segment-start-cell': context.segmentStartCell,
        'segment-end-cell': context.segmentEndCell,
        'segment-start-row': row.segmentStartRow,
        'segment-end-row': row.segmentEndRow,
        'first-segment-col': column.field === `${CommonDataTableFields.SegmentPrefix}0`,
        'mp-text-cell': isTextStyleColumn(column),
        'mp-number-cell': isNumberStyleColumn(column),
        'has-multi-select': tableEl.attr(`multi-select`),
        'first-in-pair': tableEl.attr(`has-comparison`) && !row.isComparisonRow,
        'hovered-row': tableEl.isRowHovered(context),
        'selected-cell': isSelectedCell,
        'is-empty': empty,
        'segment-block-label': Boolean(segmentCellBlock),
        selectable: isSelectable,
      }}
      attrs={{
        elref: `bodyCell`,
        ...(focusable ? {tabindex: 0} : {}),
      }}
      style={{
        '--bg-color': bgColor,
        color: textColor,
        cursor: (cellItem && cellItem.clickable) || isSelectable ? `pointer` : `auto`,
        ...(gridAreaStyle ? {'grid-area': gridAreaStyle} : {}),
      }}
      on={{
        click: (ev) =>
          tableEl.helpers.handleCellClick(ev, row, column.field, context, {
            textColor,
            bgColor,
          }),
        pointerenter: () => tableEl.helpers.handleCellPointerEnter(context),
        pointerleave: tableEl.helpers.handleCellPointerLeave,
      }}
    >
      <div sel='.body-cell-wrapper'>
        <div sel='.body-cell-content'>
          {cellCheckboxEl}
          {cellCollapseEl}
          {cellContent}
          {isSelectedCell === false && cellTooltipEl}
        </div>
        {(() => {
          if (!segmentCellBlock) {
            return null;
          }
          if (!segmentCellBlock.metricMetadatas) {
            return null;
          }
          const numDescendantRows = segmentCellBlock.flatRowEndIdx - segmentCellBlock.flatRowStartIdx;
          if (numDescendantRows <= 1) {
            return null;
          }

          return renderSegmentMetricMetadatas({
            dataColumns: tableEl.getCurrentDataColumns(),
            liftType: tableEl.attr(`lift-comparison-type`),
            metricMetadatas: segmentCellBlock.metricMetadatas,
            // Because of the smaller font-size / line-height for segment subtotal rows, it's always possible to
            // render `numDescendantRows` segment subtotal rows in addition to the segment label.
            numAvailableRows: numDescendantRows,
            i18n: tableEl.getContext(`i18n`),
          });
        })()}
      </div>
    </div>
  );
}

function renderResizeHandles({
  tableEl,
  columns,
  gap,
  idxOffset,
  resizingColumnIdx,
  resizeHandleTopOffset,
}: {
  tableEl: DataTable;
  columns: TableColumn[];
  gap: number;
  idxOffset: number;
  resizingColumnIdx: number;
  resizeHandleTopOffset: number;
}) {
  return columns.reduce((handles: VNode[], column, idx) => {
    if (!column.resizable) {
      return handles;
    }
    const handleWidth = 8;
    const globalColIdx = idx + idxOffset;
    const isResizing = resizingColumnIdx === globalColIdx;

    let handleLeft = 0;
    if (gap) {
      const gapBefore = !column.lastSticky && !column.rightmost ? gap : 0;
      handleLeft = -gapBefore - handleWidth / 2;
    } else {
      const hasStickyColumnsBorder = column.lastSticky;
      handleLeft = -(handleWidth / 2) - (hasStickyColumnsBorder ? STICKY_COLUMNS_BORDER_WIDTH / 2 : 0);
    }

    return handles.concat(
      <div
        sel={`.${CssClass.ColumnResizeHandle}`}
        class={{'is-resizing': isResizing, 'other-resizing': Number.isFinite(resizingColumnIdx) && !isResizing}}
        style={{
          'grid-column-start': `${idx + 2}`,
          left: `${handleLeft}px`,
          width: `${handleWidth}px`,
          top: `${resizeHandleTopOffset}px`,
          height: `calc(100% - ${resizeHandleTopOffset}px)`,
        }}
        on={{
          mousedown: (ev) => tableEl.onResizeMouseDown({ev, resizeColumnIndex: globalColIdx}),
        }}
      />,
    );
  }, []);
}

function renderSegmentCellBlocks({tableEl}: {tableEl: DataTable}) {
  if (!tableEl.showSegmentCellBlocks()) {
    return [];
  }

  const {atSelectionLimit} = tableEl.helpers.getGlobalSelectionStats();
  const numHeaderRows = tableEl.getNumHeaderRows();
  const {renderRowStartIdx, numRowsToRender} = tableEl.state;
  const renderRowEndIdx = renderRowStartIdx + numRowsToRender;

  return tableEl.state.segmentCellBlocks
    .filter((cellBlock) => {
      // Only render segment cell blocks that are partially/fully within the render area.
      return areIntersectingRanges(
        [cellBlock.flatRowStartIdx, cellBlock.flatRowEndIdx],
        [renderRowStartIdx, renderRowEndIdx],
      );
    })
    .map((cellBlock) => {
      const gridRowOffset = numHeaderRows;
      // Convert to grid row indices. Also clamp the start of the first segment block, and the end of the last
      // segment block so that they align with the rendered rows
      const gridRowStartIdx = gridRowOffset + cellBlock.flatRowStartIdx;
      const gridRowEndIdx = gridRowOffset + cellBlock.flatRowEndIdx;

      const column = tableEl.state.columns[cellBlock.columnIdx];
      const row = tableEl.state.rows[cellBlock.rowStartIdx];

      const hoveredRowIndex = (tableEl.state.hoveredCellContext as BodyCellRenderContext)?.rowIdx;
      const isHovered =
        hoveredRowIndex !== undefined &&
        hoveredRowIndex >= cellBlock.rowStartIdx &&
        hoveredRowIndex < cellBlock.rowEndIdx;

      return (
        <div
          sel={`.segment-cell-block`}
          style={{
            'grid-row': `${gridRowStartIdx + 1} / ${gridRowEndIdx + 1}`,
            'grid-column': `${cellBlock.columnIdx + 1} / ${cellBlock.columnIdx + 2}`,
          }}
          class={{
            'first-col': cellBlock.columnIdx === 0,
            'hovered-segment-block': isHovered,
          }}
          on={{pointerenter: tableEl.helpers.handleSegmentBlockPointerEnter}}
        >
          <div sel={`.segment-cell-block-inner`}>
            <BodyCell
              tableEl={tableEl}
              row={row}
              column={column}
              atSelectionLimit={atSelectionLimit}
              segmentCellBlock={cellBlock}
            />
          </div>
        </div>
      );
    });
}

function renderFixedMultimetricFooter({tableEl}: {tableEl: DataTable}) {
  const gridRowEnd = tableEl.getTableEndRowIdx() + 1;
  const gridRowStart = gridRowEnd - 1;
  const gridColStart = 1;
  const gridColEnd = gridColStart + tableEl.state.stickyColumnsCount;
  return (
    <div
      sel='.multimetric-footer-cell.overall-cell'
      style={{
        'grid-area': `${gridRowStart} / ${gridColStart} / ${gridRowEnd} / ${gridColEnd}`,
      }}
    >
      Overall
    </div>
  );
}

function renderDataMultimetricFooter({tableEl, columns}: {tableEl: DataTable; columns: DataColumn[]}) {
  const gridRowEnd = tableEl.getTableEndRowIdx() + 1;
  const gridRowStart = gridRowEnd - 1;
  return columns.map((column) => {
    const gridColStart = column.subsetColumnIdx + 1;
    const gridColEnd = gridColStart + 1;
    return (
      <div
        sel='.multimetric-footer-cell.metric-total-cell'
        style={{
          'grid-area': `${gridRowStart} / ${gridColStart} / ${gridRowEnd} / ${gridColEnd}`,
        }}
      >
        {renderDataCellContent({
          // The overall lift comparison value only makes sense for time comparison lift at the moment
          formattedComparisonValue: column.labelTransform(column, `comparisonOverall`),
          dataFormatType: column.dataFormatType,
          liftType: tableEl.attr(`lift-comparison-type`) === LiftType.Time ? LiftType.Time : null,
          liftValue: column.liftOverall,
          formattedValue: column.labelTransform(column, `metricOverall`),
        })}
      </div>
    );
  });
}

//#endregion

function renderSearchInput({tableEl}: {tableEl: DataTable}) {
  return (
    <InputEl
      sel={`.${CssClass.SearchInput}`}
      attrs={{
        icon: `search`,
        placeholder: `Search`,
        size: InputSize.Medium,
        'show-clear': true,
      }}
      props={{
        value: tableEl.state.searchFilter,
      }}
      on={{
        clear: () => tableEl.helpers.handleSearchTextUpdate(``),
        input: (ev: any) => tableEl.helpers.handleSearchTextUpdate(ev.target.value),
      }}
    />
  );
}

function getTemplateWidths(cols: TableColumn[]) {
  return cols.map((c) => `${c.computedWidth}px`).join(` `);
}

export function template(this: DataTable) {
  const tableTheme = this.getTheme();
  const gap = this.helpers.getGapSize();
  const groupHeaders = this.getGroupHeadersToRender();
  const fixedColumns = this.getFixedColumns();
  const dataColumns = this.getDataColumns();
  const dataColumnsToRender = this.getDataColumnsToRender();
  const fixedColumnsWidth = getSummedColumnWidth(fixedColumns);
  const internalTableWidth = this.helpers.getInternalTableWidth();
  const hasSegmentCellBlocks = this.showSegmentCellBlocks();
  const canUseResizingLayoutVal = !this.isBoardMode();
  const preTitleRowHeight = this.getPreTitleRowHeight();
  const titleRowHeight = this.getTitleRowHeight();
  const headerRowHeights = this.getHeaderRowHeights();
  const headerHeight = sum(headerRowHeights) + gap;
  const {dataColumnWidthsMatrix: columnWidthsMatrix, rowHeightsMatrix} = this.state;

  const headerDataColumnsToRender: TableColumn[] = this.getHeaderDataColumnsToRender();
  const dataColumnWidthsCssVar = columnWidthsMatrix
    .map(({width, count}) => (count === 1 ? `${width}px` : `repeat(${count},${width}px)`))
    .join(` `);
  const dataColumnsWidth = getSummedColumnWidth(headerDataColumnsToRender);

  const flatRowsToRender = this.getFlatRowsToRender();
  const rowHeightsArray = [
    ...headerRowHeights.map((x) => `${x}px`),
    ...rowHeightsMatrix.map(({height, count}) => (count === 1 ? `${height}px` : `repeat(${count},${height}px)`)),
    ...(this.showMultimetricFooter() ? [`${FOOTER_ROW_HEIGHT}px`] : []),
  ];
  const rowHeights = rowHeightsArray.join(` `);

  const useDataCellBorders = tableTheme.dataCellBorders === `line`;
  const {numSelected, numSelectable, atSelectionLimit, hasReachedSegmentationLimit} =
    this.helpers.getGlobalSelectionStats();
  const emptySearchResults = this.state.searchFilter && !flatRowsToRender.length;

  const titleRowTopOffset = (preTitleRowHeight || 0) + gap;

  return (
    // wrap the table in order to overlay tooltips on the scrollbox
    <div
      sel='.wrapper'
      style={{
        '--checkbox-cell-padding-left': `${CHECKBOX_CELL_PADDING_LEFT}px`,
        '--table-horizontal-padding': canUseResizingLayoutVal ? HORIZONTAL_CONTENT_PADDING_STYLE : `0`,
      }}
    >
      {this.attr(`multi-select`) && !canUseResizingLayoutVal
        ? (() => {
            return (
              <div sel='.segments-info-section'>
                <div sel='.truncated-segments-info'>
                  <div sel='.checkbox-wrapper'>
                    <CheckboxEl
                      sel='.select-segment-checkbox'
                      attrs={{
                        checked: atSelectionLimit,
                        'unchecked-icon': numSelected > 0 ? `dash` : ``,
                      }}
                      style={{width: `auto`}}
                      on={{click: this.helpers.toggleAllSelected}}
                    />
                    <ExperimentalTooltipEl
                      attrs={{
                        placement: Placement.RightCenter,
                        text: atSelectionLimit ? `Deselect All` : `Select Top Segments`,
                      }}
                    />
                  </div>
                  {`Selected ${numSelected} of ${hasReachedSegmentationLimit ? `top ` : ``}${numSelectable} segments`}
                </div>
                {/**
                 * TODO: Should use input element here than entire filter bar. To get the same UX functionality of
                 * the input in the filter bar element, can create a new component and share it between filter bar and here.
                 */}
                <FilterBarEl
                  sel='.filter-bar'
                  on={{
                    updateInput: (ev) => this.helpers.handleSearchTextUpdate(ev.detail.value),
                  }}
                />
              </div>
            );
          })()
        : null}
      {this.isSearchEnabled() && !groupHeaders && renderSearchInput({tableEl: this})}
      <div
        sel='.mp-data-table-container'
        hook={{
          insert: (vnode) => this.scrollParentElInserted(vnode.elm as HTMLElement),
        }}
      >
        <div
          sel='.mp-data-table'
          style={{
            '--data-table-columns': `${fixedColumnsWidth}px ${dataColumnsWidth + (useDataCellBorders ? 0 : 2 * gap)}px`,
            '--fixed-columns-width': `${fixedColumnsWidth}px`,
            '--gap': `${gap}px`,
            '--body-row-height': `${tableTheme.rowHeight}px`,
            '--sticky-columns-border-width': `${dataColumns.length ? STICKY_COLUMNS_BORDER_WIDTH : 0}px`,
            '--header-height': `${headerHeight}px`,
            '--title-row-top-offset': `${titleRowTopOffset}px`,
            '--time-comparison-header-row-top-offset': `${titleRowTopOffset + titleRowHeight}px`,
            '--table-width': `${internalTableWidth}px`,
            '--row-heights': rowHeights,
          }}
          class={{
            'use-data-cell-borders': useDataCellBorders,
            'has-group-headers': Boolean(groupHeaders),
            heatmap: tableTheme.cellBackground === `heatmap`,
            'cell-outline-on-hover': tableTheme.cellBorderHover === `outline`,
            'multimetric-styles': tableTheme.multimetricStyles,
            'has-segment-cell-blocks': hasSegmentCellBlocks,
            'resizing-layout': canUseResizingLayoutVal,
            'show-info-footer-container': emptySearchResults,
            // TODO(mack): There's some report-editor tests where sorting isn't being passed in, causing the null
            // check on sorting to be needed. Seems to be a race condition, but not exactly sure why.
            'grouped-sort': this.state.sorting?.sortBy === SortBy.Column,
            'can-use-view-n': this.canUseViewN(),
            'has-time-comparison-headers': this.hasTimeComparisonHeaders(),
          }}
          on={{
            scroll: () => this.helpers.handleScroll(),
          }}
          hook={{
            insert: (vnode) => this.scrollElInserted(vnode.elm as HTMLElement),
          }}
        >
          <div
            sel={`.${CssClass.FixedColumns}`}
            class={{
              'sticky-columns': this.helpers.shouldUseStickyColumns(),
            }}
            style={{'--fixed-column-widths': getTemplateWidths(fixedColumns)}}
            on={{pointerleave: this.helpers.handleCellPointerLeave}}
          >
            {groupHeaders ? (
              <div
                sel='.group-header-row-cell'
                style={{
                  'grid-row': `1 / 2`,
                  'grid-column': `1 / ${fixedColumns.length + 1}`,
                }}
              >
                {this.isSearchEnabled() && renderSearchInput({tableEl: this})}
              </div>
            ) : null}
            {fixedColumns.map((column) => (
              <TitleCell tableEl={this} column={column} />
            ))}
            {this.hasTimeComparisonHeaders() &&
              fixedColumns.map((column) => <TimeComparisonHeaderCell column={column} tableEl={this} />)}
            {renderSegmentCellBlocks({tableEl: this})}
            {renderBodyRows({
              tableEl: this,
              rows: flatRowsToRender,
              columns: fixedColumns,
              atSelectionLimit,
            })}
            {this.showMultimetricFooter() ? renderFixedMultimetricFooter({tableEl: this}) : null}
            {renderResizeHandles({
              tableEl: this,
              columns: fixedColumns,
              gap,
              idxOffset: 0,
              resizingColumnIdx: this.state.resizeColumnIndex,
              resizeHandleTopOffset: preTitleRowHeight || 0,
            })}
          </div>
          <div
            sel={`.${CssClass.ScrollableColumns}`}
            style={{'--data-column-widths': dataColumnWidthsCssVar}}
            class={{'is-resizing': Boolean(this.state.resizeColumnIndex)}}
            on={{pointerleave: this.helpers.handleCellPointerLeave}}
          >
            {groupHeaders
              ? groupHeaders.reduce(
                  (acc: {nodes: VNode[]; colStart: number}, {title, span, funnelStepNumber}, idx) => {
                    const context: GroupHeaderCellRenderContext = {
                      rowType: RowType.GroupHeader,
                      columnIdx: acc.colStart - 1,
                    };
                    const shouldShowTooltip =
                      isSameCellByContext(context, this.state.hoveredCellContext) &&
                      Boolean(this.state.hoveredTruncatedTextType);
                    const tooltipEl = shouldShowTooltip ? (
                      <ExperimentalTooltipEl
                        attrs={{
                          // Limit the tooltip to its own cell. This is to prevent the first grouped header's tooltip
                          // from being obscured by the sticky column to its left.
                          'max-width': `${GROUP_HEADER_MIN_WIDTH}px`,
                          placement: Placement.Bottom,
                          offset: 0,
                          'always-show': true,
                          text: title,
                        }}
                      />
                    ) : null;
                    return {
                      nodes: [
                        ...acc.nodes,
                        <div
                          sel='.group-header-row-cell.group-header'
                          class={{'last-group-header': idx === groupHeaders.length - 1}}
                          style={{
                            'grid-row': `1 / 2`,
                            'grid-column': `${acc.colStart} / ${acc.colStart + span}`,
                          }}
                        >
                          <span
                            sel='.group-header-label'
                            on={{
                              pointerenter: (ev) => {
                                this.helpers.handleCellPointerEnter(context);
                                this.helpers.handleTruncatableTextPointerEnter(ev);
                              },
                              pointerleave: this.helpers.handleCellPointerLeave,
                            }}
                          >
                            {funnelStepNumber ? <span sel='.step-number'>{funnelStepNumber}</span> : null}
                            {title}
                          </span>
                          {tooltipEl}
                        </div>,
                      ],
                      colStart: acc.colStart + span,
                    };
                  },
                  {nodes: [], colStart: 1},
                ).nodes
              : null}
            {headerDataColumnsToRender.map((col) => (
              <TitleCell tableEl={this} column={col} />
            ))}
            {this.hasTimeComparisonHeaders() &&
              dataColumns.map((column) => <TimeComparisonHeaderCell column={column} tableEl={this} />)}
            {renderBodyRows({
              tableEl: this,
              rows: flatRowsToRender,
              columns: dataColumnsToRender,
              atSelectionLimit,
            })}
            {this.showMultimetricFooter()
              ? renderDataMultimetricFooter({tableEl: this, columns: dataColumnsToRender})
              : null}
          </div>
          {canUseResizingLayoutVal && emptySearchResults ? (
            <div sel='.info-footer-container'>
              {emptySearchResults ? <div sel='.empty-search-results'>Your search returned no results</div> : null}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
